import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { GEN_AIRTABLE_VERF_CODE, SAVE_AIRTABLE_TOKEN, REMOVE_SHOP_INTEGRATION } from '../apollo/mutation';
import {
  Page,
  Card,
  AppProvider,
  Layout,
  Frame,
  Subheading,
  Button,
  Stack,
  TextField,
  Toast,
  Modal,
  FormLayout,
  Link,
  SettingToggle,
} from '@shopify/polaris';
import { GET_SETTINGS_INFO, GET_TWITTER_AUTH_URL } from '../apollo/queries';
import { TitleBar } from '@shopify/app-bridge-react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { DuplicateMinor } from '@shopify/polaris-icons';
import { Redirect } from '@shopify/app-bridge/actions';
import { v4 as uuidv4 } from 'uuid';
import Loader from 'react-loader-spinner';
import { propOr, isEmpty, isNil, pathOr } from 'ramda';

const Integrations = ({ app, shopifyToken, shopifyDomain }) => {
  const [genCode] = useMutation(GEN_AIRTABLE_VERF_CODE);
  const [saveToken] = useMutation(SAVE_AIRTABLE_TOKEN);
  const [removeShopIntegration] = useMutation(REMOVE_SHOP_INTEGRATION);
  const loading = false;

  const [airTableVerification, setAirTableVerification] = useState('');
  const [airTableApiKey, setAirTableApiKey] = useState('');
  const [airTableApiKeyModal, setAirTableApiKeyModal] = useState(false);
  const [toastStatus, setToastStatus] = useState({ status: false, message: '' });
  const [form, setForm] = useState({});
  const [shop, setShop] = useState({});

  const toggleAirtableApiKeyModal = () => setAirTableApiKeyModal(!airTableApiKeyModal);
  const generateAirTableVerification = () => {
    genCode({ variables: { shopifyToken } })
      .then((response) => {
        setAirTableVerification(response.data.genAirtableVerfCode.code)
      })
  };
  const saveAirtableApiKey = () => {
    saveToken({ variables: { shopifyToken, token: form.airTableApiKey } })
      .then((response) => {
        if (response.data.saveAirtableToken.status === 'ok') {
          setAirTableApiKey(form.airTableApiKey);
          setForm({ ...form, airTableApiKey: '', respOk: true });
          toggleAirtableApiKeyModal();
        }
      })
  };

  const removeIntegration = (type) => {
    setForm({ ...form, respOk: false });
    removeShopIntegration({ variables: {
      shopifyToken, token: form.airTableApiKey,
      type: type } })
    .then((response) => {
      setShop(response.data.removeShopIntegration.shop);
    })
  }

  const { data } = useQuery(GET_SETTINGS_INFO, {
    variables: { shopifyToken },
  });

  const { data: twitterAuthUrl = {}} = useQuery(GET_TWITTER_AUTH_URL, {
    variables: { shopifyToken }
  });

  useEffect(() => {
    if (!isEmpty(shopData)) {
      setShop(shopData);
    }
  });

  const twitterUrl = pathOr({}, ['twitterAuthUrl', 'url'])(twitterAuthUrl);

  const linkedinUrl = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${process.env.LINKEDIN_CLIENT_ID}&redirect_uri=${process.env.HOST_URL}&state=${process.env.LINKEDIN_STATE_SECRET}&scope=r_liteprofile%20r_emailaddress%20w_member_social%20r_organization_social%20w_organization_social%20rw_organization_admin`
  const isSubscribedUser = !isEmpty(shop.currentSubscription);
  const shopData = propOr({}, 'shop')(data);

  const redirect = Redirect.create(app);

  const hasAirtableToken = shop.airtableTokenExisting || form.respOk;

  const hasTwitterToken = shop.twitterToken ? 'Remove' : 'Connect';
  const hasLinkedinToken = shop.linkedinToken ? 'Remove' : 'Connect';
  const hasAirtableTokenLabel = hasAirtableToken ? 'Remove' : 'Add Airtable API Key';

  const updateIntegrationTwitter = () => {
    if(shop.twitterToken){
      removeIntegration('twitter')
    }else{
      localStorage.setItem('shopifyToken', shop.shopifyDomain);
      redirect.dispatch(Redirect.Action.REMOTE, twitterUrl);
    }
  }

  const updateIntegrationLinkedin = () => {
    if(shop.linkedinToken){
      removeIntegration('linkedin')
    }else{
      localStorage.setItem('shopifyToken', shop.shopifyDomain);
      redirect.dispatch(Redirect.Action.REMOTE, linkedinUrl);
    }
  }

  const updateIntegrationAirtable = () => {
    if(hasAirtableToken){
      removeIntegration('airtable')
    }else{
      toggleAirtableApiKeyModal();
    }
  }

  if (!loading) {
    return (
      <AppProvider>
        <Frame>
          <TitleBar title='Integrations' />
          <Page title='Integrations'>
            <Layout>
              {toastStatus.status && (
                <Toast content={toastStatus.message} onDismiss={() => setToastStatus({ status: false, message: '' })} />
              )}
              <Layout.AnnotatedSection
                title='Airtable'
                description={
                  <p style={{ marginBottom: '10px' }}>
                    Synch ShopHire with Airtable to build a powerful applicant tracking system.
                  </p>
                }>
                <Card sectioned>
                  <Stack vertical>
                    <Subheading>STEP 1: GENERATE SHOPHIRE VERIFICATION CODE</Subheading>
                    <Button onClick={generateAirTableVerification}>{'Generate'}</Button>
                    {!!airTableVerification && (
                      <TextField
                        label='Verification Code'
                        disabled={true}
                        value={airTableVerification}
                        connectedLeft={
                          <CopyToClipboard
                            text={airTableVerification}
                            onCopy={() => {
                              setToastStatus({
                                status: true,
                                message: 'Copied to clipboard',
                              });
                            }}>
                            <Button icon={DuplicateMinor} />
                          </CopyToClipboard>
                        }
                      />
                    )}
                    <Subheading>STEP 2: ENTER AIRTABLE API KEY</Subheading>
                    <SettingToggle
                      action={{
                        content: hasAirtableTokenLabel,
                        onAction: updateIntegrationAirtable
                      }}>
                      Authorize Shophire to post updates to your company's Airtable is <b>
                      { hasAirtableToken ? 'enabled' : 'disabled'}</b>.
                    </SettingToggle>
                  </Stack>
                </Card>
              </Layout.AnnotatedSection>
              <Layout.AnnotatedSection
                title='Twitter'
                description={
                  <p style={{ marginBottom: '10px' }}>
                    Engage your Twitter follower by sharing new job openings
                    {!isSubscribedUser && <Link onClick={() => changeRoute('/account-plan')}>Upgrade account plan</Link>}
                  </p>}
                >
                <SettingToggle
                  action={{
                    content: hasTwitterToken,
                    onAction: updateIntegrationTwitter,
                    disabled: twitterUrl ? false : true
                  }}>
                  Authorize Shophire to post updates to your company's Twitter is <b>
                    { shop.twitterToken ? 'enabled' : 'disabled'}</b>.
                </SettingToggle>
              </Layout.AnnotatedSection>
              <Layout.AnnotatedSection
                title='Linkedin'
                description={
                  <p style={{ marginBottom: '10px' }}>
                    Broadcast your job opportunities and promote your employer brand on the world's
                    largest profesional network.
                    {!isSubscribedUser && <Link onClick={() => changeRoute('/account-plan')}>Upgrade account plan</Link>}
                  </p>}
                >
                <SettingToggle
                  action={{
                    content: hasLinkedinToken,
                    onAction: updateIntegrationLinkedin
                  }}>
                  Authorize Shophire to post updates to your company's Linkedin is <b>
                    { shop.linkedinToken ? 'enabled' : 'disabled'}</b>.
                </SettingToggle>
             </Layout.AnnotatedSection>
            </Layout>
            <Modal
              title={'Add Airtable API Key'}
              open={airTableApiKeyModal}
              onClose={() => toggleAirtableApiKeyModal()}
              primaryAction={{
                content: 'Save',
                disabled: !form.airTableApiKey,
                onAction: saveAirtableApiKey,
              }}
              secondaryActions={[
                {
                  content: 'Cancel',
                  onAction: () => toggleAirtableApiKeyModal(),
                },
              ]}
              loading={false}>
              <Modal.Section>
                <FormLayout>
                  <TextField
                    id='airtableApiKey'
                    label='Airtable API Key'
                    value={form.airTableApiKey}
                    name='airtableApiKey'
                    onChange={(value) => setForm({ ...form, airTableApiKey: value })}
                  />
                </FormLayout>
                <br />
              </Modal.Section>
            </Modal>
          </Page>
        </Frame>
      </AppProvider>
    );
  } else {
    return (
      <AppProvider>
        <div
          style={{
            width: '100%',
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
          <Loader type='TailSpin' color='#6371c7' height={80} width={80} />
        </div>
      </AppProvider>
    );
  }
};

export default Integrations;
